import { useState } from 'react';
import { listCommissions, listVideos, type VideoRow } from '../api';
import { useAsync } from '../lib/async';
import { FamilyFilter, byFamily } from '../lib/family';
import { Pager, ScanNote, useCursorList } from '../lib/paging';
import {
  Empty,
  Id,
  Link,
  LocalFilterNote,
  NotBuilt,
  Pill,
  Result,
  StatusPill,
  Toolbar,
  when,
} from '../lib/ui';

/**
 * Every episode, across every family.
 *
 * What this screen can and cannot do is worth stating plainly, because the
 * limits are deliberate in one case and missing plumbing in the other.
 *
 * **Deliberate:** the finished film is not playable here. A family's episode is
 * reachable only through the family API behind that family's own login, and the
 * staff API has no playback route for one. An admin panel that can quietly watch
 * a child's videos is a different product with a different consent story.
 *
 * **Missing plumbing:** there is no `GET /admin/videos/{videoId}`, so per-segment
 * provenance — which asset served which segment — is not exposed. The list gives
 * `segments` as a count and nothing more. What IS reachable is the commission
 * behind the video, and from there the assets it generated, each of which is
 * pool art and watchable in place. That is the review path, and it is one click
 * longer than it should be. Said out loud rather than hidden behind a row that
 * expands into nothing.
 *
 * `/admin/videos` also has no `family` parameter and no cursor — it is still a
 * capped scan taking only `limit` — so the family filter here runs in the
 * browser over one page, and the screen says so every time it is used.
 */
export function Videos({ initialFamily }: { initialFamily?: string }) {
  const [family, setFamily] = useState(initialFamily ?? '');
  const [open, setOpen] = useState<string | null>(null);

  const list = useCursorList<VideoRow>(() => listVideos({ limit: 200 }), []);

  return (
    <>
      <Toolbar>
        <FamilyFilter value={family} onChange={setFamily} />
        <span className="spacer" />
      </Toolbar>

      <Result of={list.result} what="videos" retry={list.reload}>
        {(page) => {
          const rows = byFamily(page.rows, family);
          return (
            <>
              <Toolbar>
                <Pager list={list} what="videos" shown={rows.length} />
              </Toolbar>
              <LocalFilterNote
                active={Boolean(family)}
                what="videos"
                why="/admin/videos takes only a limit — it has no family parameter and no cursor."
              />
              <p className="muted small">
                Metadata only. Playback of a finished episode stays on the family
                surface, behind the parent's own credentials — the staff API has no
                route for it. To review what a video is actually made of, open its
                commission: the generated clips are pool assets and play in place.
              </p>

              {!rows.length ? (
                <Empty what="videos" filtered={Boolean(family)} />
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Family</th>
                      <th>Status</th>
                      <th className="num">Segments</th>
                      <th>Reuse</th>
                      <th>Created</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((video) => (
                      <tr key={video.videoId}>
                        <td>
                          {video.title || <span className="muted">untitled</span>}
                          <div>
                            <Id value={video.videoId} chars={12} />
                          </div>
                        </td>
                        <td>
                          {video.familyId ? (
                            <Link to={{ name: 'families', familyId: video.familyId }}>
                              <Id value={video.familyId} />
                            </Link>
                          ) : (
                            <Pill tone="bad">no family</Pill>
                          )}
                        </td>
                        <td>
                          <StatusPill status={video.status} />{' '}
                          {video.stitched ? (
                            <Pill tone="on">stitched</Pill>
                          ) : (
                            <Pill title="served as a playlist of segments rather than one file">
                              playlist
                            </Pill>
                          )}
                          {video.moderation?.flags?.length ? (
                            <div className="warn small">
                              flagged: {video.moderation.flags.join(', ')}
                            </div>
                          ) : null}
                        </td>
                        <td className="num muted">{video.segments}</td>
                        <td className="muted small nowrap">
                          {video.reuse?.segments
                            ? `${video.reuse.reused ?? 0}/${video.reuse.segments} reused`
                            : ''}
                        </td>
                        <td className="muted nowrap">{when(video.createdAt)}</td>
                        <td>
                          <button
                            className="link"
                            onClick={() => setOpen(open === video.videoId ? null : video.videoId)}
                          >
                            {open === video.videoId ? 'close' : 'trace'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <ScanNote listing={page} />
              {open && (
                <Trace
                  videoId={open}
                  familyId={rows.find((v) => v.videoId === open)?.familyId}
                />
              )}
            </>
          );
        }}
      </Result>
    </>
  );
}

/**
 * Find the commission that produced this video.
 *
 * There is no reverse index from video to commission, but commission rows carry
 * `videoId` and the byFamily GSI makes a family's commissions a Query rather
 * than a scan — so given the family this is one cheap read and a match in the
 * browser. Without a family it is not answerable cheaply and the screen says so
 * instead of running an unbounded scan on somebody's behalf.
 */
function Trace({ videoId, familyId }: { videoId: string; familyId?: string }) {
  const { result, reload } = useAsync(
    () =>
      familyId
        ? listCommissions({ family: familyId, limit: 200 })
        : Promise.resolve({ rows: [], count: 0 }),
    [videoId, familyId ?? ''],
  );

  if (!familyId) {
    return (
      <section className="detail">
        <h2>Trace this video</h2>
        <NotBuilt
          what="Finding the commission for a video with no family"
          detail="Commission rows carry videoId, but there is no index on it. With a familyId the byFamily GSI makes this one Query; without one it is a full scan, which this screen will not run on its own initiative."
        />
      </section>
    );
  }

  return (
    <section className="detail">
      <h2>Trace this video</h2>
      <Result of={result} what="the commission behind this video" retry={reload}>
        {(page) => {
          const match = page.rows.find((row) => row.videoId === videoId);
          if (!match) {
            return (
              <>
                <Empty what="commission for this video" filtered />
                <p className="muted small">
                  No commission in this family's most recent page references this
                  videoId. It may be older than the page, or the video may predate
                  the link being written.
                </p>
              </>
            );
          }
          return (
            <p>
              Made by{' '}
              <Link to={{ name: 'commission', commissionId: match.commissionId }}>
                “{match.request || match.commissionId}”
              </Link>{' '}
              — the commission page carries the shot plan, the failure reason if
              there was one, and every generated clip with a player.
            </p>
          );
        }}
      </Result>
    </section>
  );
}
